import { z } from 'zod';
import { NOTIFICATION_CHANNEL_IDS } from './communication-channel.registry.js';
import { DETAILED_REPORT_KEYS } from './detailed-report-profile.schema.js';
import { getDetailedReportDefinition } from './report-catalog.js';
import {
    REPORT_PROFILE_SCHEMA_VERSION,
    ReportProfilePublicWriteSchema,
} from './report-profile.schema.js';

export const DETAILED_REPORT_SCHEMA_VERSION = 2;
export const DETAILED_REPORT_SNAPSHOT_SCHEMA_VERSION = 1;
export const DETAILED_REPORT_SNAPSHOT_V2_SCHEMA_VERSION = 2;
export const DETAILED_REPORT_AI_PROMPT_VERSION = 'detailed-report-narrative-v1';
export const DETAILED_REPORT_NOTIFICATION_CHANNELS = NOTIFICATION_CHANNEL_IDS;
export const DETAILED_REPORT_JOB_REVISION_VERSION = 3;
export const DETAILED_REPORT_JOB_APPEARANCE_STRATEGY = 'tenant-global-at-execution-v1';
export const DETAILED_REPORT_CANONICALIZER_VERSION = 1;
export const DETAILED_REPORT_COMPARATOR_VERSION = 1;

export const DetailedReportAiConfigurationSchema = z.object({
    enabled: z.boolean(),
    available: z.boolean(),
    status: z.enum(['ready', 'disabled', 'unavailable']),
    reason: z.enum(['feature-disabled', 'provider-unavailable', 'tenant-disabled']).nullable(),
    provider: z.object({
        endpointConfigured: z.boolean(),
        apiKeyConfigured: z.boolean(),
        modelConfigured: z.boolean(),
    }).strict(),
    tenantPolicy: z.object({
        aiEnabled: z.boolean(),
        aiAllowNames: z.boolean(),
    }).strict().nullable(),
}).strict();

const persistedIdSchema = z.preprocess(
    (value) => (value === undefined || value === null ? value : String(value)),
    z.string().trim().min(1).max(80),
);

export const DetailedReportJobProfileRevisionV3Schema = ReportProfilePublicWriteSchema.pick({
    schemaVersion: true,
    profileVersion: true,
    reportKey: true,
    definitionVersionAtSave: true,
    profileContract: true,
    name: true,
    locale: true,
    status: true,
    scope: true,
    selectorDefaults: true,
    confidentiality: true,
    options: true,
}).extend({
    id: persistedIdSchema,
    schemaVersion: z.literal(REPORT_PROFILE_SCHEMA_VERSION),
}).strict();

export const DetailedReportJobProfileRevisionSchema = DetailedReportJobProfileRevisionV3Schema;

const selectorQueryScalar = z.union([z.string().max(500), z.number().finite(), z.boolean()]);
export const DetailedReportJobSelectorRevisionSchema = z.object({
    tenantId: persistedIdSchema,
    mode: z.enum(['filtered', 'selected', 'single']),
    recordId: persistedIdSchema.optional(),
    recordIds: z.array(persistedIdSchema).min(1).max(5000).optional(),
    query: z.object({
        search: z.string().max(200).optional(),
        filters: z.record(z.string(), z.array(selectorQueryScalar).max(100)).optional(),
        serverFilters: z.record(
            z.string(),
            z.union([selectorQueryScalar, z.array(selectorQueryScalar).max(100)]),
        ).optional(),
        sortBy: z.string().trim().max(80).optional(),
        sortOrder: z.enum(['asc', 'desc']).optional(),
    }).strict().optional(),
    selectorDigest: z.string().regex(/^[a-f0-9]{64}$/),
}).strict().superRefine((value, ctx) => {
    if (value.mode === 'single' && !value.recordId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recordId'], message: 'Single selector requires recordId' });
    }
    if (value.mode !== 'single' && value.recordId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recordId'], message: 'recordId is only valid for single selectors' });
    }
    if (value.mode === 'selected' && !value.recordIds?.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recordIds'], message: 'Selected selector requires recordIds' });
    }
    if (value.mode !== 'selected' && value.recordIds) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recordIds'], message: 'recordIds are only valid for selected selectors' });
    }
});

export const DetailedReportJobInitiatingSubjectSchema = z.object({
    userId: persistedIdSchema,
    tenantId: persistedIdSchema,
}).strict();

export const DetailedReportSnapshotSubjectSchema = z.object({
    recordIds: z.array(persistedIdSchema).max(5000)
        .transform((recordIds) => [...new Set(recordIds)].sort()),
    selectorDigest: z.string().regex(/^[a-f0-9]{64}$/),
}).strict();

export const DetailedReportSnapshotV2EnvelopeSchema = z.object({
    schemaVersion: z.literal(DETAILED_REPORT_SNAPSHOT_V2_SCHEMA_VERSION),
    reportKey: z.enum(DETAILED_REPORT_KEYS),
    definitionVersion: z.number().int().positive(),
    adapterKey: z.string().trim().min(1).max(120),
    adapterVersion: z.number().int().positive(),
    canonicalizerVersion: z.number().int().positive(),
    comparatorVersion: z.number().int().positive(),
    subject: DetailedReportSnapshotSubjectSchema,
    canonicalData: z.record(z.string(), z.unknown()),
}).strict();

export const DetailedReportBaselineSchema = z.object({
    strategy: z.enum(['none', 'last-successful', 'previous-month', 'previous-quarter', 'selected']).default('none'),
    snapshotId: z.string().trim().min(1).max(80).optional(),
}).strict().superRefine((value, ctx) => {
    if (value.strategy === 'selected' && !value.snapshotId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['snapshotId'], message: 'Selected baseline requires snapshotId' });
    }
    if (value.strategy !== 'selected' && value.snapshotId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['snapshotId'], message: 'snapshotId is only valid for selected baselines' });
    }
});

export const GenerateDetailedReportSchema = z.object({
    reportKey: z.enum(DETAILED_REPORT_KEYS),
    profileId: z.string().trim().min(1).max(80),
    expectedProfileVersion: z.number().int().positive().optional(),
    recordId: z.string().trim().min(1).max(80).optional(),
    locale: z.enum(['tr', 'en']).optional(),
    title: z.string().trim().min(1).max(200).optional(),
    search: z.string().max(200).optional(),
    filters: z.record(z.string(), z.array(z.union([z.string(), z.number(), z.boolean()]))).optional(),
    serverFilters: z.record(z.string(), z.union([
        z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number(), z.boolean()])),
    ])).optional(),
    sortBy: z.string().trim().max(80).optional(),
    sortOrder: z.enum(['asc', 'desc']).optional(),
    filterSummary: z.string().max(4000).optional(),
    scope: z.object({
        mode: z.enum(['filtered', 'selected', 'single']),
        recordIds: z.array(z.string().trim().min(1).max(80)).min(1).max(5000).optional(),
    }).strict().optional(),
    baseline: DetailedReportBaselineSchema.optional(),
    createSnapshot: z.boolean().default(true),
    narrativeId: z.string().trim().min(1).max(80).optional(),
}).strict().superRefine((value, ctx) => {
    const definition = getDetailedReportDefinition(value.reportKey);
    const dossier = definition?.kind === 'dossier';
    if (dossier && !value.recordId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recordId'], message: 'Dossier report requires recordId' });
    }
    if (!dossier && value.recordId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recordId'], message: 'recordId is only valid for dossier reports' });
    }
});

const scheduleShape = {
    frequency: z.enum(['weekly', 'monthly', 'quarterly', 'cron']),
    timezone: z.string().trim().min(1).max(100).default('Europe/Istanbul'),
    dayOfWeek: z.number().int().min(0).max(6).optional(),
    dayOfMonth: z.number().int().min(1).max(28).optional(),
    hour: z.number().int().min(0).max(23).default(8),
    minute: z.number().int().min(0).max(59).default(0),
    cron: z.string().trim().min(9).max(120).optional(),
};

export const DetailedReportScheduleSchema = z.object(scheduleShape).strict().superRefine((value, ctx) => {
    if (value.frequency === 'cron' && !value.cron) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['cron'], message: 'Cron schedule requires an expression' });
    }
    if (value.frequency === 'weekly' && value.dayOfWeek === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['dayOfWeek'], message: 'Weekly schedule requires dayOfWeek' });
    }
});

const recipientSchema = z.object({
    userId: z.string().trim().min(1).max(80),
    delivery: z.enum(['authenticated-link', 'attachment']).default('authenticated-link'),
}).strict();

const recipientTargetSchema = z.object({
    type: z.enum(['user', 'team', 'unit']),
    value: z.string().trim().min(1).max(80),
    label: z.string().trim().min(1).max(200).optional(),
}).strict();

const detailedReportJobObjectSchema = z.object({
    name: z.string().trim().min(1).max(120),
    profileId: z.string().trim().min(1).max(80),
    reportKey: z.enum(DETAILED_REPORT_KEYS),
    recordId: z.string().trim().min(1).max(80).optional(),
    recordIds: z.array(z.string().trim().min(1).max(80)).min(1).max(5000)
        .refine((recordIds) => new Set(recordIds).size === recordIds.length, { message: 'Selected record ids must be unique' })
        .optional(),
    schedule: DetailedReportScheduleSchema,
    baselineStrategy: z.enum(['last-successful', 'previous-month', 'previous-quarter']).default('last-successful'),
    format: z.enum(['pdf', 'docx']).default('pdf'),
    recipients: z.array(recipientSchema).min(1).max(100).optional(),
    recipientTargets: z.array(recipientTargetSchema).min(1).max(100)
        .refine((targets) => (
            new Set(targets.map((target) => `${target.type}:${target.value.toLowerCase()}`)).size === targets.length
        ), { message: 'Recipient targets must be unique' })
        .optional(),
    notificationChannels: z.array(z.enum(DETAILED_REPORT_NOTIFICATION_CHANNELS))
        .min(1)
        .max(DETAILED_REPORT_NOTIFICATION_CHANNELS.length)
        .refine((channels) => new Set(channels).size === channels.length, {
            message: 'Notification channels must be unique',
        }),
    attachmentPolicy: z.enum(['links-only', 'allow']).default('links-only'),
    enabled: z.boolean().default(true),
}).strict();

export const CreateDetailedReportJobSchema = detailedReportJobObjectSchema.superRefine((value, ctx) => {
    if (!value.recipients?.length && !value.recipientTargets?.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['recipientTargets'],
            message: 'At least one recipient or recipient target is required',
        });
    }
    const dossier = getDetailedReportDefinition(value.reportKey)?.kind === 'dossier';
    if (dossier && !value.recordId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recordId'], message: 'Dossier job requires recordId' });
    }
    if (!dossier && value.recordId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recordId'], message: 'recordId is only valid for dossier jobs' });
    }
    if (dossier && value.recordIds) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['recordIds'], message: 'recordIds are only valid for portfolio jobs' });
    }
});

export const UpdateDetailedReportJobSchema = detailedReportJobObjectSchema.partial().extend({
    expectedVersion: z.number().int().positive(),
}).strict().refine((value) => Object.keys(value).some((key) => key !== 'expectedVersion'), {
    message: 'At least one job field must be updated',
});

export const DetailedReportLifecycleSchema = z.object({
    action: z.enum(['submit-review', 'approve', 'cancel']),
    expectedRevision: z.number().int().positive(),
    comment: z.string().trim().max(1000).optional(),
}).strict();

const citationSchema = z.object({
    type: z.enum(['metric', 'delta', 'record', 'methodology']),
    key: z.string().trim().min(1).max(200),
    snapshotId: z.string().trim().min(1).max(80).optional(),
    recordId: z.string().trim().min(1).max(80).optional(),
}).strict();

export const DetailedReportNarrativeSentenceSchema = z.object({
    text: z.string().trim().min(1).max(1000),
    citations: z.array(citationSchema).min(1).max(12),
}).strict();

export const GenerateDetailedReportNarrativeSchema = z.object({
    snapshotId: z.string().trim().min(1).max(80),
    baselineSnapshotId: z.string().trim().min(1).max(80).optional(),
    locale: z.enum(['tr', 'en']).default('tr'),
    tone: z.enum(['executive', 'neutral', 'concise']).default('executive'),
    length: z.enum(['short', 'medium', 'long']).default('medium'),
}).strict();

export const UpdateDetailedReportNarrativeSchema = z.object({
    expectedVersion: z.number().int().positive(),
    sentences: z.array(DetailedReportNarrativeSentenceSchema).min(1).max(20).optional(),
    action: z.enum(['accept', 'reject']).optional(),
}).strict().refine((value) => value.sentences || value.action, {
    message: 'Narrative sentences or action is required',
});
